import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router';
import { api } from '../lib/api';
import { useLang, KIND_EN } from '../lib/i18n';
import { dayLong, dayShort, hhmm, istMidnight, tl, weekdayShort, dateNum, nf } from '../lib/format';
import type { EventSummary, EventsResponse } from '../lib/types';
import { AppHeader, ErrorView, Loading } from '../components/AppHeader';
import { Poster } from '../components/brand';
import { MkSignature } from '../components/MkSignature';

const DAY = 86400_000;
const MON_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const MON_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function Home() {
  const { tx, lang } = useLang();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<EventsResponse | null>(null);
  const [error, setError] = useState(false);
  const cat = params.get('cat') || 'Tümü';
  const city = params.get('city') || 'Tümü';
  const day = Number(params.get('day') || 0); // 0 = whole week, 1 = today, 2 = tomorrow…
  const set = (k: string, v: string | number) => {
    const p = new URLSearchParams(params);
    if (!v || v === 'Tümü') p.delete(k);
    else p.set(k, String(v));
    setParams(p, { replace: true });
  };

  const loc = useLocation();
  useEffect(() => {
    api<EventsResponse>('/events').then(setData, () => setError(true));
  }, []);
  useEffect(() => {
    if (loc.hash === '#mekanlar' && data) document.getElementById('mekanlar')?.scrollIntoView({ behavior: 'smooth' });
  }, [data, loc.hash, loc.key]);

  const today = useMemo(() => istMidnight(data?.now ?? Date.now()), [data]);
  const dayStart = (d: number) => today + (d - 1) * DAY;
  const inDay = (t: number, d: number) => t >= dayStart(d) && t < dayStart(d) + DAY;

  if (error) return <ErrorView msg={tx('Program yüklenemedi.', 'Couldn’t load the programme.')} action={<button className="btn btn-line" onClick={() => location.reload()}>{tx('Tekrar dene', 'Try again')}</button>} />;
  if (!data) return <Loading />;

  const kindLabel = (k: string) => (lang === 'en' ? KIND_EN[k] || k : k);
  const cats = ['Tümü', 'Tiyatro', 'Stand-up', 'Konser', 'Çocuk'];
  const list = data.events.filter(
    (e) => (cat === 'Tümü' || e.kind === cat) && (city === 'Tümü' || e.venue.city === city) && (!day || e.sessions.some((s) => inDay(s.startsAt, day))),
  );
  const feat = data.events.find((e) => e.badge?.c === 'hot') || data.events[0]!;
  const end = today + 7 * DAY;
  const range =
    lang === 'en'
      ? `${dateNum(today)} – ${dateNum(end)} ${MON_EN[new Date(end + 3 * 3600_000).getUTCMonth()]}`
      : `${dateNum(today)} – ${dateNum(end)} ${MON_TR[new Date(end + 3 * 3600_000).getUTCMonth()]}`;
  const open = (e: EventSummary) => {
    const s = e.sessions.find((x) => x.status !== 'sold' && (!day || inDay(x.startsAt, day))) || e.sessions[0];
    nav(`/e/${e.slug}${s ? `?s=${s.id}` : ''}`);
  };
  const lodos = data.venues.find((v) => v.id === 'sahne-lodos');

  return (
    <div className="scroll">
      <AppHeader cur="home" city={city} onCity={(c) => set('city', c)} />
      <div className="wrap">
        <section className="home-hero" style={{ display: 'grid', gap: 20, padding: '14px 0 6px' }}>
          <div>
            <p className="cap muted" style={{ margin: '0 0 10px' }}>
              {range} · {data.events.length} {tx('etkinlik', 'events')} · {data.venues.length} {tx('sahne', 'stages')}
            </p>
            <h1 className="d-xl home-h" style={{ margin: 0 }}>
              {lang === 'en' ? (
                <>
                  What’s <span className="serif-i" style={{ fontWeight: 400, letterSpacing: '-.02em' }}>on stage</span>
                  <br />
                  this week?
                </>
              ) : (
                <>
                  Bu hafta
                  <br />
                  <span className="serif-i" style={{ fontWeight: 400, letterSpacing: '-.02em' }}>
                    sahnede
                  </span>{' '}
                  ne var?
                </>
              )}
            </h1>
          </div>
          <button type="button" className="feat" onClick={() => open(feat)} style={{ all: 'unset', cursor: 'pointer', display: 'block', position: 'relative' }} aria-label={feat.title}>
            <Poster poster={feat.poster} title={feat.short || feat.title} kind={kindLabel(feat.kind)} venue={feat.venue.name} cls="feat-poster" titleSize={64} />
            <span className="badge" style={{ position: 'absolute', left: 14, bottom: -12, background: 'var(--card)', boxShadow: 'var(--sh-1)' }}>
              {tx('Haftanın seçkisi', 'Pick of the week')} · {feat.venue.name}
            </span>
          </button>
        </section>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 30, position: 'sticky', top: -12, background: 'var(--paper)', padding: '12px 0 10px', zIndex: 4 }}>
          <div className="chips" role="group" aria-label={tx('Kategori', 'Category')}>
            {cats.map((c) => (
              <button key={c} type="button" className="chip" aria-pressed={cat === c} onClick={() => set('cat', c)}>
                {kindLabel(c)}
              </button>
            ))}
          </div>
          <div className="dates" role="group" aria-label={tx('Tarih', 'Date')}>
            <button type="button" className="date all" aria-pressed={day === 0} onClick={() => set('day', 0)}>
              <span className="dw">{tx('Tüm', 'All')}</span>
              <span className="dd" style={{ fontSize: 16 }}>
                {tx('hafta', 'week')}
              </span>
            </button>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((d) => {
              const t = dayStart(d);
              const has = data.events.some((e) => e.sessions.some((s) => inDay(s.startsAt, d)));
              return (
                <button key={d} type="button" className="date" aria-pressed={day === d} aria-label={dayLong(t + 12 * 3600_000, lang)} onClick={() => set('day', d)}>
                  <span className="dw">{d === 1 ? tx('Bugün', 'Today') : weekdayShort(t + 12 * 3600_000, lang)}</span>
                  <span className="dd">{dateNum(t + 12 * 3600_000)}</span>
                  <span className={`pip ${has ? '' : 'none'}`} />
                </button>
              );
            })}
          </div>
        </div>
        <div className="sec-h">
          <h2 className="d-m">{day ? dayLong(dayStart(day) + 12 * 3600_000, lang) : tx('Bu hafta', 'This week')}</h2>
          <span className="muted t-s">
            {list.length} {tx('etkinlik', list.length === 1 ? 'event' : 'events')}
          </span>
        </div>
        <div className="egrid">
          {list.map((e) => {
            const s0 = e.sessions.filter((s) => !day || inDay(s.startsAt, day))[0] || e.sessions[0]!;
            return (
              <button key={e.id} type="button" className="ecard" onClick={() => open(e)}>
                <Poster poster={e.poster} title={e.short || e.title} kind={kindLabel(e.kind)} venue={e.venue.name} />
                <div>
                  <div className="cap muted">
                    {kindLabel(e.kind)} · {dayShort(s0.startsAt, lang)} · {hhmm(s0.startsAt)}
                  </div>
                  <h3>{e.title}</h3>
                  <div className="meta">
                    {e.venue.name}, {e.venue.area}
                  </div>
                  <div className="row">
                    <span className="price">{lang === 'en' ? `From ${tl(e.fromKurus, lang)}` : `${tl(e.fromKurus)}’den`}</span>
                    {e.badge && <span className={`badge ${e.badge.c}`}>{lang === 'en' ? e.badge.en : e.badge.t}</span>}
                  </div>
                </div>
              </button>
            );
          })}
          {!list.length && (
            <p className="muted" style={{ padding: '24px 0' }}>
              {tx('Bu filtreyle etkinlik yok. Başka bir gün dene.', 'No events for this filter. Try another day.')}
            </p>
          )}
        </div>
        <div id="mekanlar" className="panel-flat" style={{ margin: '36px 0 16px', display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <div className="d-s">Sahne Lodos, Kadıköy</div>
            <div className="muted t-s">
              {lodos?.license ?? 420} {tx('koltuk · Parter, Balkon, 4 loca · Tekerlekli sandalye erişimi', 'seats · Stalls, Balcony, 4 boxes · Wheelchair access')}
            </div>
          </div>
          <button type="button" className="btn btn-line btn-sm" onClick={() => nav('/e/marti')}>
            {tx('Programı gör', 'See programme')}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10, marginBottom: 28 }}>
          {data.venues
            .filter((v) => v.id !== 'sahne-lodos')
            .map((v) => (
              <button key={v.id} type="button" className="tk-row" style={{ gridTemplateColumns: '1fr auto', padding: '12px 14px' }} onClick={() => set('city', v.city)}>
                <div>
                  <h4 style={{ fontSize: 16 }}>{v.name}</h4>
                  <div className="t-s muted">
                    {v.area}, {v.city} · {nf(v.license, lang)} {tx('koltuk', 'seats')}
                  </div>
                </div>
              </button>
            ))}
        </div>
        <footer className="t-s muted" style={{ borderTop: '1px solid var(--rule)', padding: '18px 0 40px', display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <span>
            {tx('Gişe · demo ürün. Test kartıyla gerçek ödeme alınmadan satın alabilirsin; veriler her gece sıfırlanır.', 'Gişe · demo product. Buy with the test card, nothing is charged; data resets nightly.')}
          </span>
          <span style={{ display: 'flex', gap: 14 }}>
            <Link to="/mekan">{tx('Mekân paneli', 'Venue panel')}</Link>
            <Link to="/kapi">{tx('Kapı okuyucu', 'Door scanner')}</Link>
            <Link to="/design">{tx('Tasarım sistemi', 'Design system')}</Link>
          </span>
          <span style={{ flexBasis: '100%', display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
            <MkSignature lang={lang} />
          </span>
        </footer>
      </div>
    </div>
  );
}
